import React, { useCallback, useMemo, useState } from "react";
import { showNotification } from "@shared/store/actions";
import { bytesToMegaBytes } from "@shared/utils";
import { FileUploader } from "react-drag-drop-files";
import { useDispatch } from "react-redux";

import { Icon } from "../Icon";
import { FileZoneUploadButton, FileZoneUploadedFileInfo } from "./components";
import { Button } from "../Common";

import "./index.scss";

const FILE_TYPES = ["csv"];

interface ImportModalProps {
  maxFileSize?: number;
  onClose: () => void;
  onSubmit: (file: File) => void;
  templateFile: string;
}

const ImportModal = (props: ImportModalProps) => {
  const { maxFileSize, onClose, onSubmit, templateFile } = props;
  const dispatch = useDispatch();

  const [file, setFile] = useState<File | null>(null);

  const fileZoneText = useMemo(() => {
    return file
      ? "Your CSV file is uploaded, now you can save it or remove and select another file"
      : "Drag & Drop your CSV file here or";
  }, [file]);

  const fileZoneIcon = useMemo(() => {
    return file ? "success" : "big-import";
  }, [file]);

  const handleTypeError = useCallback(() => {
    dispatch(
      showNotification({
        message: "Please use a template for the browsing file",
        appearance: "error",
      }),
    );
  }, [dispatch]);

  const handleChange = useCallback(
    (file: File | null) => {
      if (file && maxFileSize && maxFileSize < bytesToMegaBytes(file.size)) {
        dispatch(
          showNotification({
            message: `The file size exceeds the maximum limit of ${maxFileSize} MB.`,
            appearance: "error",
          }),
        );
        return;
      }

      setFile(file);
    },
    [dispatch, maxFileSize],
  );

  const handleSubmitFile = useCallback(() => {
    if (file) {
      onSubmit(file);
      setFile(null);
    }
  }, [file, onSubmit]);

  return (
    <div className="import-csv-file-modal modal-content-wrapper">
      <div className="modal-heading">Import CSV</div>
      <div className="modal-content">
        <div>
          <FileUploader
            handleChange={handleChange}
            name="file"
            types={FILE_TYPES}
            fileOrFiles={file}
            onTypeError={handleTypeError}
            disabled={file}
          >
            <div className="upload-file-zone">
              <div className="file-zone-icon">
                <Icon type={fileZoneIcon} />
              </div>
              {file ? <div className="file-zone-title">Success!</div> : null}
              <div className="file-zone-text">{fileZoneText}</div>
              <div className="file-zone-action">
                {file ? (
                  <FileZoneUploadedFileInfo file={file} onDeleteFile={() => setFile(null)} />
                ) : (
                  <FileZoneUploadButton />
                )}
              </div>
            </div>
          </FileUploader>
        </div>
      </div>
      <div className="modal-actions">
        <div className="download-template">
          <a className="download-template" href={templateFile} download="Template.csv">
            Download Template
          </a>
        </div>
        <div>
          <Button appearance="subtle" className="subtle auto-width" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!file} className="blue auto-width" onClick={handleSubmitFile}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
